import { useCallback, useEffect, useState } from 'react';

export type LocationPermissionState = 'granted' | 'prompt' | 'denied' | 'unsupported';

/**
 * Location permission state for the lobby checklist. Location is best-effort
 * (the walkthrough map, CAD-6600): the customer can always join without it,
 * but pre-granting here avoids a mid-call browser prompt. Tracks the live
 * PermissionStatus so a flip in the browser's own site controls updates the
 * row without a reload.
 */
export default function useLocationPermission(enabled: boolean) {
  const [state, setState] = useState<LocationPermissionState>(() =>
    navigator.geolocation ? 'prompt' : 'unsupported',
  );

  useEffect(() => {
    if (!enabled || !navigator.geolocation) {
      return;
    }
    let disposed = false;
    let status: PermissionStatus | undefined;
    navigator.permissions
      ?.query({ name: 'geolocation' })
      .then((permissionStatus) => {
        if (disposed) {
          return;
        }
        status = permissionStatus;
        setState(permissionStatus.state);
        permissionStatus.onchange = () => setState(permissionStatus.state);
      })
      .catch(() => {
        // Permissions API missing (older Safari): stay promptable.
      });
    return () => {
      disposed = true;
      if (status) {
        status.onchange = null;
      }
    };
  }, [enabled]);

  const request = useCallback(
    () =>
      new Promise<void>((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('geolocation unsupported'));
          return;
        }
        navigator.geolocation.getCurrentPosition(
          () => {
            setState('granted');
            resolve();
          },
          (error) => {
            setState('denied');
            reject(error);
          },
          { timeout: 10000 },
        );
      }),
    [],
  );

  return { state, request };
}
